import { FC, useEffect, useId } from 'react';
import { Control, useController } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronDownIcon, InfoCircle } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../../../../components/combobox/index';
import Loader from '../../../../../../../components/loader/index';
import { Cursor, Tooltip } from '../../../../../../../components/tooltip/index';
import { useAllSharePlans } from '../../../../../../../hooks/share-plan/use-all-share-plans';
import { selectedCompanyStorage } from '../../../../../../../storage/company/selected-company.util';
import { MappingEditorStore } from '../../../../store/mapping-editor.store';
import { AddTemplateFormSchema } from '../../../../validations/add-template.validation';

export type EquityPlanComboboxProps = {
  control: Control<AddTemplateFormSchema>;
  disabled?: boolean;
};

type EquityPlanOptionListProps = {
  control: Control<AddTemplateFormSchema>;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isLoading: boolean;
  scrollableTargetId: string;
  sharePlanIdsAndNames: { id: string; name: string }[];
};

const EquityPlanOptionList: FC<EquityPlanOptionListProps> = ({
  control,
  fetchNextPage,
  hasNextPage,
  isLoading,
  scrollableTargetId,
  sharePlanIdsAndNames,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader iconClassName="size-8" />
      </div>
    );
  }
  if (sharePlanIdsAndNames.length === 0) {
    return (
      <span className="py-2 text-sm font-normal text-gray-700">
        <AppFormattedMessage id="NO_PLAN_FOUND" />
      </span>
    );
  }
  return (
    <InfiniteScroll
      className="flex flex-col gap-1"
      dataLength={sharePlanIdsAndNames.length}
      hasMore={hasNextPage}
      loader={
        <div className="flex justify-center py-2">
          <Loader iconClassName="size-8" />
        </div>
      }
      next={fetchNextPage}
      scrollableTarget={scrollableTargetId}
    >
      {sharePlanIdsAndNames.map(({ id, name }) => (
        <FormComboboxOption control={control} key={id} name="stepOne.equityPlan" value={id}>
          <span className="truncate text-sm font-normal text-gray-700">{name}</span>
        </FormComboboxOption>
      ))}
    </InfiniteScroll>
  );
};

export const EquityPlanCombobox: FC<EquityPlanComboboxProps> = ({ control, disabled }) => {
  const scrollableTargetId = useId();
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const { fetchNextPage, hasNextPage, isPending, sharePlans } = useAllSharePlans({ companyId });
  const setSelectedEquityPlan = MappingEditorStore(
    ({ setSelectedEquityPlan }) => setSelectedEquityPlan,
  );

  const { field } = useController({ control, name: 'stepOne.equityPlan' });
  const sharePlanIdsAndNames = sharePlans.map((p) => ({ id: p.id, name: p.name }));
  const selectedPlan = sharePlans.find((p) => p.id === field.value);
  const displayValue = selectedPlan?.name ?? '';

  useEffect(() => {
    if (disabled) {
      setSelectedEquityPlan(null);
      return;
    }
    setSelectedEquityPlan(selectedPlan ?? null);
  }, [disabled, selectedPlan, setSelectedEquityPlan]);

  return (
    <FormCombobox className="relative" control={control} name="stepOne.equityPlan">
      <div className="relative">
        <FormComboboxInput
          control={control}
          customValue={() => displayValue}
          disabled={disabled}
          icon={<ChevronDownIcon className="mt-3 h-4 w-4 text-gray-500" />}
          name="stepOne.equityPlan"
          placeholder={<AppFormattedMessage id="EQUITY_PLAN" />}
          placeholderTrailing={
            <Tooltip
              className="w-[480px]"
              content={<AppFormattedMessage id="EQUITY_PLAN_MAPPINGS_TOOLTIP" />}
              cursor={Cursor.POINTER}
              placement="top"
            >
              <button
                aria-label="Info"
                className="mb-[2px] text-gray-400 hover:text-gray-600 focus:outline-none"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                type="button"
              >
                <InfoCircle className="size-3" iconColor="currentColor" />
              </button>
            </Tooltip>
          }
          readOnly
          wrapperClassName="min-h-[56px]"
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" disabled={disabled} />
      </div>
      <ComboboxOptions
        className="absolute z-10 max-h-[280px] w-full overflow-y-auto rounded border border-gray-200 bg-white p-2 shadow-lg"
        id={scrollableTargetId}
      >
        <EquityPlanOptionList
          control={control}
          fetchNextPage={fetchNextPage}
          hasNextPage={!!hasNextPage}
          isLoading={isPending}
          scrollableTargetId={scrollableTargetId}
          sharePlanIdsAndNames={sharePlanIdsAndNames}
        />
      </ComboboxOptions>
    </FormCombobox>
  );
};
